import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { createStyles, withStyles, darken, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { useContent } from 'hooks';
import { CustomSVG } from 'components';
import { ShareDialog } from 'features/sharing';

const styles = (theme: Theme) =>
  createStyles({
    button: {
      padding: 0,
      border: '1px solid transparent',

      '&:hover svg': {
        color: theme.palette.common.white,
      },
    },
    shareButton: {
      '&:hover svg': {
        backgroundColor: darken(theme.palette.secondary.main, 0.1),
      },
    },
    icon: {
      color: theme.palette.common.white,
      padding: theme.spacing(0.5),
      borderRadius: '50%',
    },
    shareIcon: {
      backgroundColor: theme.palette.secondary.main,
    },
    hasShared: {
      backgroundColor: theme.custom.palette.grey1,
    },
    importantShareButton: {
      padding: `0 ${theme.spacing(2)}px`,
    },
    importantShareIcon: {
      color: theme.palette.common.white,
    },
  });

const Share: FC<WithStyles<typeof styles>> = ({ classes }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const content = useContent();

  function getButton() {
    const { important_to_share } = content;

    let button = renderShareButton();

    if (important_to_share) {
      button = renderImportantShareButton();
    }

    return button;
  }

  function handleOpen() {
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  function renderShareButton() {
    const buttonClasses = classNames(classes.button, classes.shareButton);
    const iconClasses = classNames(classes.icon, classes.shareIcon, {
      [classes.hasShared]: content.has_requesting_user_shared,
    });

    return (
      <Button
        className={buttonClasses}
        onClick={handleOpen}
        endIcon={<CustomSVG className={iconClasses} name="arrow" size={32} />}>
        {t('common:share')}
      </Button>
    );
  }

  function renderImportantShareButton() {
    const buttonClasses = classNames(classes.importantShareButton, {
      [classes.hasShared]: content.has_requesting_user_shared,
    });

    return (
      <Button
        className={buttonClasses}
        variant="contained"
        color="secondary"
        size="small"
        onClick={handleOpen}
        endIcon={<CustomSVG className={classes.importantShareIcon} name="arrow" size={32} />}>
        {t('content:sharePost')}
      </Button>
    );
  }

  return (
    <Fragment>
      {getButton()}
      <ShareDialog onClose={handleClose} open={open} />
    </Fragment>
  );
};

export default withStyles(styles)(Share);
